import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';

import {
    activeSubAccountSelector,
    createMarkerShare,
    deleteMarkerShare,
    changeView,
    commitSubAccountMappings,
} from '../../features/subaccounts/subaccountSlice';
import { markersSelector } from '../../features/places/placesSlice';
import SelectionDraftActions from './SelectionDraftActions';

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
        overflow: 'hidden',
    },
    dialogTitle: {
        padding: '20px 24px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    titleText: {
        fontWeight: 700,
        letterSpacing: '0.02em',
    },
    subtitleText: {
        fontSize: '0.8rem',
        color: 'rgba(226, 232, 240, 0.8)',
    },
    searchField: {
        minWidth: 240,
        backgroundColor: 'rgba(248, 250, 252, 0.95)',
        borderRadius: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiInputBase-input': {
            fontSize: '0.9rem',
        },
    },
    closeButton: {
        padding: 6,
        borderRadius: 10,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#e2e8f0',
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
        },
    },
    content: {
        padding: 20,
        paddingTop: 36,
    },
    panel: {
        borderRadius: 16,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        minHeight: 320,
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    panelTitle: {
        fontWeight: 600,
        color: '#0f172a',
    },
    selectAllButton: {
        textTransform: 'none',
        borderRadius: 10,
        fontWeight: 600,
        background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)',
        color: '#ffffff',
        boxShadow: '0 10px 18px rgba(14, 165, 233, 0.2)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
        },
    },
    listGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 12,
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    },
    itemShared: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(15, 23, 42, 0.7)',
        color: '#f8fafc',
    },
    itemText: {
        fontSize: '0.92rem',
        fontWeight: 600,
        color: 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emptyText: {
        color: '#64748b',
        fontSize: '0.85rem',
    },
}));

const SubAccountMarkers = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const markers = useSelector(markersSelector);
    const activeSubAccount = useSelector(activeSubAccountSelector);

    const [markerList, setMarkerList] = useState([]);
    const [baseMarkerList, setBaseMarkerList] = useState([]);
    const [initializedSubuserId, setInitializedSubuserId] = useState(null);
    const [search, setSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const subuserId = activeSubAccount?.subuser_id;

    useEffect(() => {
        if (!subuserId || !Array.isArray(activeSubAccount?.markers) || initializedSubuserId === subuserId) {
            return;
        }
        const selectedMarkerIds = [...new Set(activeSubAccount.markers.map((m) => `${m.marker_id}`))];
        setBaseMarkerList(selectedMarkerIds);
        setMarkerList(selectedMarkerIds);
        setInitializedSubuserId(subuserId);
        setSaveError('');
    }, [activeSubAccount?.markers, initializedSubuserId, subuserId]);

    const handleToggle = (markerId, nextChecked) => {
        if (!subuserId || isSaving) {
            return;
        }
        const shouldShare = typeof nextChecked === 'boolean'
            ? nextChecked
            : markerList.indexOf(markerId) === -1;
        if (!shouldShare) {
            setMarkerList((prev) => prev.filter((id) => id !== markerId));
        } else {
            setMarkerList((prev) => [...new Set([...prev, markerId])]);
        }
        setSaveError('');
    };

    const allMarkerIds = useMemo(() => Object.keys(markers || {}), [markers]);
    const allSelected = allMarkerIds.length > 0 && allMarkerIds.every((id) => markerList.indexOf(id) !== -1);

    const handleSelectAllToggle = () => {
        if (!subuserId || isSaving) {
            return;
        }
        if (allSelected) {
            setMarkerList([]);
        } else {
            setMarkerList(allMarkerIds);
        }
        setSaveError('');
    };

    const addedMarkerIds = useMemo(() => {
        const baseSet = new Set(baseMarkerList);
        return markerList.filter((id) => !baseSet.has(id));
    }, [baseMarkerList, markerList]);

    const removedMarkerIds = useMemo(() => {
        const draftSet = new Set(markerList);
        return baseMarkerList.filter((id) => !draftSet.has(id));
    }, [baseMarkerList, markerList]);

    const hasPendingChanges = addedMarkerIds.length > 0 || removedMarkerIds.length > 0;

    const handleDiscard = () => {
        if (isSaving) {
            return;
        }
        setMarkerList(baseMarkerList);
        setSaveError('');
    };

    const handleSubmit = async () => {
        if (!subuserId || !hasPendingChanges || isSaving) {
            return false;
        }

        let persistedMarkerIds = baseMarkerList;
        setIsSaving(true);
        setSaveError('');
        try {
            if (removedMarkerIds.length > 0) {
                await dispatch(deleteMarkerShare({ subuser_id: subuserId, markerids: removedMarkerIds })).unwrap();
                persistedMarkerIds = persistedMarkerIds.filter((id) => !removedMarkerIds.includes(id));
                dispatch(commitSubAccountMappings({ subuser_id: subuserId, markers: persistedMarkerIds }));
            }
            if (addedMarkerIds.length > 0) {
                await dispatch(createMarkerShare({ subuser_id: subuserId, markerids: addedMarkerIds })).unwrap();
            }
            dispatch(commitSubAccountMappings({ subuser_id: subuserId, markers: markerList }));
            setBaseMarkerList(markerList);
            return true;
        } catch (error) {
            setBaseMarkerList(persistedMarkerIds);
            setSaveError('Some changes may have been saved. Please retry to finish applying this selection.');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const visibleMarkersList = useMemo(() => {
        if (!markers) {
            return [];
        }
        if (search.length > 2) {
            const matches = Object.keys(markers).filter((id) =>
                markers[id].marker_name.toUpperCase().includes(search.toUpperCase())
            );
            return [...new Set([...matches, ...markerList])];
        }
        return Object.keys(markers);
    }, [markers, search, markerList]);

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        if (hasPendingChanges && !window.confirm('Discard unsaved marker changes?')) {
            return;
        }
        dispatch(changeView('main'));
    };

    if (!markers || Object.keys(markers).length === 0 || !activeSubAccount) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleRow}>
                    <Box>
                        <Typography variant="h6" className={classes.titleText}>
                            Marker Selection
                        </Typography>
                        <Typography className={classes.subtitleText}>
                            {activeSubAccount?.person_name || 'Sub Account'}
                        </Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <TextField
                            className={classes.searchField}
                            size="small"
                            variant="outlined"
                            placeholder="Search markers"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon path={mdiMagnify} size={0.8} color="#64748b" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton onClick={handleClose} className={classes.closeButton}>
                            <Icon path={mdiClose} size={0.8} color="currentColor" />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Box className={classes.panel}>
                    <Box className={classes.panelHeader}>
                        <Typography className={classes.panelTitle}>
                            Markers Shared - {markerList.length}
                        </Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Typography variant="caption" color="text.secondary">
                                {visibleMarkersList.length} total
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleSelectAllToggle}
                                variant="contained"
                                className={classes.selectAllButton}
                                disabled={isSaving}
                            >
                                {allSelected ? 'Clear All' : 'Share All'}
                            </Button>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className={classes.listGrid}>
                        {visibleMarkersList.length > 0 ? (
                            visibleMarkersList.map((markerId) => {
                                const marker = markers[markerId];
                                if (!marker) {
                                    return null;
                                }
                                const isShared = markerList.indexOf(markerId) !== -1;
                                return (
                                    <Box
                                        key={markerId}
                                        className={`${classes.item} ${isShared ? classes.itemShared : ''}`}
                                    >
                                        <Typography className={classes.itemText}>
                                            {marker.marker_name}
                                        </Typography>
                                        <Switch
                                            checked={isShared}
                                            onChange={(event, checked) => handleToggle(markerId, checked)}
                                            disabled={isSaving}
                                            sx={{
                                                '& .MuiSwitch-thumb': {
                                                    color: isShared ? '#f8fafc' : '#0ea5e9',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: isShared
                                                        ? 'rgba(248, 250, 252, 0.35)'
                                                        : 'rgba(148, 163, 184, 0.4)',
                                                },
                                                '& .Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: isShared ? '#94a3b8' : '#38bdf8',
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography className={classes.emptyText}>
                                No markers found.
                            </Typography>
                        )}
                    </Box>
                    <SelectionDraftActions
                        itemLabel="marker"
                        additions={addedMarkerIds}
                        removals={removedMarkerIds}
                        isSaving={isSaving}
                        saveError={saveError}
                        onDiscard={handleDiscard}
                        onSubmit={handleSubmit}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SubAccountMarkers;
